export type TKeyValue = TKey['value'];

export type TKey =
  | {
      type: 'KLANK';
      value:
        | 'a'
        | 'e'
        | 'o'
        | 'u'
        | 'i'
        | 'aa'
        | '(a)a'
        | 'ee'
        | '(e)e'
        | 'oo'
        | '(o)o'
        | 'uu'
        | '(u)u'
        | 'ei'
        | 'ij'
        | 'ie'
        | 'au'
        | 'ou'
        | 'oe'
        | 'eu'
        | 'ui'
        | 'uw'
        | 'ch'
        | 'ng'
        | 'nk'
        | 'aai'
        | 'ooi'
        | 'oei'
        | 'auw'
        | 'ouw'
        | 'eeuw'
        | 'ieuw'
        | 'b'
        | 'd'
        | 'f'
        | 'g'
        | 'h'
        | 'j'
        | 'k'
        | 'l'
        | 'm'
        | 'n'
        | 'p'
        | 'r'
        | 's'
        | 't'
        | 'v'
        | 'w'
        | 'bb'
        | 'dd'
        | 'ff'
        | 'gg'
        | 'hh'
        | 'jj'
        | 'kk'
        | 'll'
        | 'mm'
        | 'nn'
        | 'pp'
        | 'rr'
        | 'ss'
        | 'tt'
        | 'vv'
        | 'ww'
        | 'zz'
        | 'z'
        | 'c'
        | 'x'
        | 'y'
        | 'q'
        | 'sjwa'
        | 'ig'
        | 'lijk';
    }
  | { type: 'GROUP'; value: '▯' | '▭' | '||' | '|||' | '||||' | '■' | '◆' | '⌒'; isHint?: boolean }
  | { type: 'SPACE'; value: '⎵' }
  | { type: 'KLANKVOET'; value: '<' | '>' }
  | { type: 'RULE'; value: '←' | '→' | '-' | '+' | '+■' | '?' };

export class RidSyllabicKeyboardUtils {
  public static areKeysEqual(key1: TKey, key2: TKey): boolean {
    return key1.type === key2.type && ('value' in key1 && 'value' in key2 ? key1.value === key2.value : true);
  }

  public static getKeyGroupName(key: TKey): keyof typeof keyboardGroups {
    for (const group of Object.keys(keyboardGroups) as (keyof typeof keyboardGroups)[]) {
      if (keyboardGroups[group].some((k) => RidSyllabicKeyboardUtils.areKeysEqual(k, key))) {
        return group;
      }
    }
    throw new Error(`Key not found in any group: ${JSON.stringify(key)}`);
  }

  public static combineMasks(a: TKeyValue[] | undefined, b: TKeyValue[] | undefined): TKeyValue[] | undefined {
    if (!a && !b) return undefined;

    if (!a) return b;
    if (!b) return a;

    const combined: TKeyValue[] = [];

    for (const key of a) {
      if (b.includes(key)) {
        combined.push(key);
      }
    }

    return combined;
  }

  public static convertValueToKey(value: string): TKey {
    if (isTyped(value, ['▯', '▭', '||', '|||', '||||', '■', '◆', '⌒'])) {
      return { type: 'GROUP', value };
    }

    if (
      isTyped(value, [
        'a',
        'e',
        'o',
        'u',
        'i',
        'aa',
        '(a)a',
        'ee',
        '(e)e',
        'oo',
        '(o)o',
        'uu',
        '(u)u',
        'ei',
        'ij',
        'ie',
        'au',
        'ou',
        'oe',
        'eu',
        'ui',
        'uw',
        'ch',
        'ng',
        'nk',
        'aai',
        'ooi',
        'oei',
        'auw',
        'ouw',
        'eeuw',
        'ieuw',
        'b',
        'd',
        'f',
        'g',
        'h',
        'j',
        'k',
        'l',
        'm',
        'n',
        'p',
        'r',
        's',
        't',
        'v',
        'w',
        'z',
        'c',
        'x',
        'y',
        'q',
        'sjwa',
        'ig',
        'lijk',
      ])
    ) {
      return { type: 'KLANK', value };
    }

    if (isTyped(value, ['<', '>'])) {
      return { type: 'KLANKVOET', value };
    }

    if (isTyped(value, ['←', '→', '-', '+', '+■'])) {
      return { type: 'RULE', value };
    }

    if (isTyped(value, ['⎵'])) {
      return { type: 'SPACE', value: '⎵' };
    }

    if (isTyped(value, ['?'])) {
      return { type: 'RULE', value: '?' };
    }

    throw new Error(`Unknown key value: ${value}`);
  }
}

export function isTyped<T extends string>(value: string, options: T[]): value is T {
  return options.includes(value as T);
}

export const keyboardGroups = {
  KLINKERS: [
    { type: 'GROUP', value: '▯' },
    { type: 'KLANK', value: 'a' },
    { type: 'KLANK', value: 'e' },
    { type: 'KLANK', value: 'o' },
    { type: 'KLANK', value: 'u' },
    { type: 'KLANK', value: 'i' },
  ],
  LANGE_KLINKERS: [
    { type: 'GROUP', value: '▭' },
    { type: 'KLANK', value: 'aa' },
    { type: 'KLANK', value: 'ee' },
    { type: 'KLANK', value: 'oo' },
    { type: 'KLANK', value: 'uu' },
  ],
  KORTE_LANGE_KLINKERS: [
    { type: 'KLANK', value: '(a)a' },
    { type: 'KLANK', value: '(e)e' },
    { type: 'KLANK', value: '(o)o' },
    { type: 'KLANK', value: '(u)u' },
  ],
  SPACE: [{ type: 'SPACE', value: '⎵' }],
  DUBBELE_KLINKERS: [
    { type: 'GROUP', value: '||' },
    { type: 'KLANK', value: 'ei' },
    { type: 'KLANK', value: 'ij' },
    { type: 'KLANK', value: 'ie' },
    { type: 'KLANK', value: 'au' },
    { type: 'KLANK', value: 'ou' },
    { type: 'KLANK', value: 'oe' },
    { type: 'KLANK', value: 'eu' },
    { type: 'KLANK', value: 'ui' },
    { type: 'KLANK', value: 'uw' },
    { type: 'KLANK', value: 'ch' },
    { type: 'KLANK', value: 'ng' },
    { type: 'KLANK', value: 'nk' },
  ],
  DRIEDUBBELE_KLINKERS: [
    { type: 'GROUP', value: '|||' },
    { type: 'KLANK', value: 'aai' },
    { type: 'KLANK', value: 'ooi' },
    { type: 'KLANK', value: 'oei' },
    { type: 'KLANK', value: 'auw' },
    { type: 'KLANK', value: 'ouw' },
  ],
  VIERDUBBELE_KLINKERS: [
    { type: 'GROUP', value: '||||' },
    { type: 'KLANK', value: 'eeuw' },
    { type: 'KLANK', value: 'ieuw' },
  ],
  MEDEKLINKERS: [
    { type: 'GROUP', value: '■' },
    { type: 'KLANK', value: 'b' },
    { type: 'KLANK', value: 'd' },
    { type: 'KLANK', value: 'f' },
    { type: 'KLANK', value: 'g' },
    { type: 'KLANK', value: 'h' },
    { type: 'KLANK', value: 'j' },
    { type: 'KLANK', value: 'k' },
    { type: 'KLANK', value: 'l' },
    { type: 'KLANK', value: 'm' },
    { type: 'KLANK', value: 'n' },
    { type: 'KLANK', value: 'p' },
    { type: 'KLANK', value: 'r' },
    { type: 'KLANK', value: 's' },
    { type: 'KLANK', value: 't' },
    { type: 'KLANK', value: 'v' },
    { type: 'KLANK', value: 'w' },
    { type: 'KLANK', value: 'z' },
  ],
  EXOTEN: [
    { type: 'GROUP', value: '◆' },
    { type: 'KLANK', value: 'c' },
    { type: 'KLANK', value: 'x' },
    { type: 'KLANK', value: 'y' },
    { type: 'KLANK', value: 'q' },
  ],
  SJWAS: [
    { type: 'GROUP', value: '⌒' },
    { type: 'KLANK', value: 'sjwa' },
    { type: 'KLANK', value: 'ig' },
    { type: 'KLANK', value: 'lijk' },
  ],
  KLANKVOET: [
    { type: 'KLANKVOET', value: '>' },
    { type: 'KLANKVOET', value: '<' },
  ],
  RULE: [
    { type: 'RULE', value: '←' },
    { type: 'RULE', value: '→' },
    { type: 'RULE', value: '-' },
    { type: 'RULE', value: '+' },
  ],
} satisfies Record<string, TKey[]>;
export const allKeys: TKey[] = Object.values(keyboardGroups).flat();

export type TKeyboardLayout = Record<keyof typeof keyboardGroups, 'ENABLED' | 'HIDDEN' | 'DISABLED'>;
